import type { State, Options, Plugins } from './types';

const stateProps = {
  width: {
    window: 'innerWidth',
    element: 'offsetWidth',
  },
  height: {
    window: 'innerHeight',
    element: 'offsetHeight',
  },
  scrollX: {
    window: 'scrollX',
    element: 'scrollLeft',
  },
  scrollY: {
    window: 'scrollY',
    element: 'scrollTop',
  },
};

export default class WindowState {
  $state: State;
  options: Options;
  plugins: Plugins;
  targetType: 'window' | 'element';

  constructor(options: Partial<Options> = {}, plugins: Partial<Plugins> = {}) {
    // NOTE: Consider structuredClone https://github.com/zloirock/core-js#structuredclone
    this.options = {
      target: window,
      breakpoints: options.breakpoints || [],
      on: { ...options.on },
      classes: {
        scrollDisable: 'is-disable-scroll',
        ...options.classes,
      },
    };

    this.targetType = this.options.target instanceof Window ? 'window' : 'element';

    this.plugins = {
      uaParser: null,
      ...plugins,
    };

    this.$state = {
      width: this.getState('width'),
      height: this.getState('height'),
      scrollX: this.getState('scrollX'),
      scrollY: this.getState('scrollY'),
      scrollbars: this._getScrollbars(),
      breakpoints: [],
      current: null,
      lastScroll: {
        directionX: null,
        directionY: null,
      },
      browser: this.plugins.uaParser ? new this.plugins.uaParser(navigator.userAgent).getResult() : null,
      device: {
        isTouch:
          'ontouchstart' in window || navigator.maxTouchPoints > 0 || window.matchMedia('(pointer:coarse)').matches,
      },
      state: 'beforeInit',
    };

    this._bind();
    this._initialize();
  }

  get state() {
    return this.$state;
  }

  get breakpoint() {
    if (!this.$state.breakpoints.length) return null;
    return this.$state.breakpoints.find((it) => it.matches) || null;
  }

  private getState(prop: 'width' | 'height' | 'scrollX' | 'scrollY') {
    // @ts-ignore
    return this.options.target[stateProps[prop][this.targetType]];
  }

  _initialize() {
    this._updateBreakpoint();
    this.$state.state = 'initialized';
  }

  _getScrollbars() {
    if (!document.body) {
      console.warn('[WindoState]', '"DOMContentLoaded" or later is recommended');
    }
    return {
      x: {
        width: this.getState('height') - document.documentElement.clientHeight,
        max: !document.body
          ? 0
          : document.documentElement.scrollWidth -
            this.getState('width') -
            (this.getState('width') - document.body.clientWidth),
      },
      y: {
        width: !document.body ? 0 : this.getState('width') - document.body.clientWidth,
        max: !document.body
          ? 0
          : document.documentElement.scrollHeight -
            this.getState('height') -
            (this.getState('height') - document.documentElement.clientHeight),
      },
    };
  }

  _bind() {
    this.getState = this.getState.bind(this);

    this._onLoad = this._onLoad.bind(this);
    this._onScroll = this._onScroll.bind(this);
    this._onResize = this._onResize.bind(this);
    this._updateBreakpoint = this._updateBreakpoint.bind(this);
    this.scrollDisable = this.scrollDisable.bind(this);
    this.scrollRelease = this.scrollRelease.bind(this);
    // this.breakpoint = this.breakpoint.bind(this);

    window.addEventListener('load', this._onLoad);
    this.options.target.addEventListener('scroll', this._onScroll, { passive: true });
    this.options.target.addEventListener('resize', this._onResize, { passive: true });
  }

  _onLoad() {
    if (this.options.on.load) {
      this.options.on.load(this.$state);
    }
  }

  _onScroll() {
    const lastScrollX = this.$state.scrollX;
    const lastScrollY = this.$state.scrollY;

    this.$state.lastScroll.directionX = this.getState('scrollX') > lastScrollX ? 'right' : 'left';
    this.$state.lastScroll.directionY = this.getState('scrollY') > lastScrollY ? 'down' : 'up';

    this.$state.scrollX = this.getState('scrollX');
    this.$state.scrollY = this.getState('scrollY');
    this.$state.scrollbars = this._getScrollbars();

    if (this.options.on.scroll) {
      this.options.on.scroll(this.$state);
    }
  }

  _onResize() {
    const oldBreakpoint = this.breakpoint;

    this.$state.width = this.getState('width');
    this.$state.height = this.getState('height');
    this.$state.scrollbars = this._getScrollbars();

    this._updateBreakpoint();
    const newBreakpoint = this.breakpoint;

    if (this.options.on.resize) {
      this.options.on.resize(this.$state);
    }

    if (oldBreakpoint?.id !== newBreakpoint?.id && this.options.on.changeBreakpoint) {
      this.options.on.changeBreakpoint(this.$state);
    }
  }

  _updateBreakpoint() {
    this.$state.breakpoints = this.options.breakpoints.map((it: any) => ({
      id: it.id,
      matches: window.matchMedia(it.query).matches,
    }));
    this.$state.current = this.$state.breakpoints.find((it: any) => it.matches) || null;
  }

  scrollDisable() {
    document.body.style.paddingRight = `${this.$state.scrollbars.y.width}px`;
    document.documentElement.classList.add(this.options.classes.scrollDisable);
  }

  scrollRelease() {
    document.documentElement.classList.remove(this.options.classes.scrollDisable);
    document.body.style.paddingRight = '';
  }
}
